import React from 'react';

import { FilterRange, InfoTooltip } from '@hcs/design-system';
import { TableCell, TableHeaderCell } from '@hcs/design-system';
import {
  PropertyStateFilterProps,
  TableCellProps,
  TableHeaderCellProps,
} from '@hcs/types';
import {
  PropertyStateArgsProps,
  PropertyStateFieldConfig,
  PropertyStateFields,
  PropertyStatePaths,
  PropertyStateTableCellProps,
  PropertyStateTableHeaderCellProps,
} from '@hcs/types';
import { formatNumber, formatSqFt } from '@hcs/utils';

// Common styles for living area types
import styles from '../livingArea/livingArea.module.css';

const FIELD = PropertyStateFields.livingAreaBelowGrade;
type FieldConfig = PropertyStateFieldConfig<typeof FIELD>;
const label = 'Sq. Ft. Below Grade';
const labelShort = 'Sq. Ft. Below Grade';
const propertyStatePath: PropertyStatePaths =
  '/propertyDetails/livingAreaBelowGrade';
const getValue: FieldConfig['getValue'] = (propertyStateArgs) => {
  const { propertyState, propertyStateType } = propertyStateArgs || {};
  if (propertyStateType === 'flat') {
    return propertyState?.[FIELD];
  }
  return propertyStateType === 'Core'
    ? propertyState?.propertyDetails?.livingAreaBelowGrade
    : undefined;
};
const formatValue: FieldConfig['formatValue'] = (propertyStateArgs) =>
  formatSqFt(getValue(propertyStateArgs));

// For displaying multiple fields in a single line
const labelMicro: FieldConfig['labelMicro'] = 'ft²';
const formatValueShort: FieldConfig['formatValueShort'] = (propertyStateArgs) =>
  formatNumber(getValue(propertyStateArgs));

const formatInline: FieldConfig['formatInline'] = (propertyStateArgs) =>
  `${formatValue(propertyStateArgs)} ${labelShort}`;
// Component for displaying a field from a schema
// eslint-disable-next-line react-refresh/only-export-components
const Display = ({ propertyStateArgs }: PropertyStateArgsProps) => {
  return <>{formatValue(propertyStateArgs)}</>;
};

// eslint-disable-next-line react-refresh/only-export-components
const Filter = ({
  spatialFiltersInput,
  onChange,
  className,
  disabled,
  hideLabels,
}: PropertyStateFilterProps<typeof FIELD>) => {
  return (
    <FilterRange
      className={className}
      disabled={disabled}
      hideLabels={hideLabels}
      maxVal={999999}
      initialValue={{
        min: spatialFiltersInput.minSqft
          ? formatNumber(spatialFiltersInput.minSqft)
          : '',
        max: spatialFiltersInput.maxSqft
          ? formatNumber(spatialFiltersInput.maxSqft)
          : '',
      }}
      onBlur={(value) => {
        onChange({
          field: FIELD,
          spatialFilterInputs: {
            minSqft: value.min === '' ? null : Number(value.min),
            maxSqft: value.max === '' ? null : Number(value.max),
          },
        });
      }}
      dataHcName={`${FIELD}-filter`}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const HeaderCell = (props: PropertyStateTableHeaderCellProps) => {
  return (
    <TableHeaderCell {...props}>
      <span className={styles.HeaderCellLabel}>LABG (ft²)</span>
      <InfoTooltip
        size="small"
        dataHcName="living-area-below-grade-info-tooltip"
        isLargeTooltip
        label={'Living Area Below Grade'}
      />
    </TableHeaderCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultHeaderProps: Partial<TableHeaderCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableHeaderCell: true,
};
HeaderCell.defaultProps = defaultHeaderProps;

// eslint-disable-next-line react-refresh/only-export-components
const ContentCell = ({
  propertyStateArgs,
  ...tableCellProps
}: PropertyStateTableCellProps) => {
  return (
    <TableCell {...tableCellProps}>
      {formatNumber(getValue(propertyStateArgs))}
    </TableCell>
  );
};
// Using default props allows for child.props checks
// in the Table components to work properly
const defaultContentProps: Partial<TableCellProps> = {
  align: 'right',
  // Needed so child component type-checking passes
  isTableCell: true,
};
ContentCell.defaultProps = defaultContentProps;

export const LIVING_AREA_BELOW_GRADE_CONFIG: FieldConfig = {
  field: FIELD,
  label,
  labelShort,
  propertyStatePath,
  getValue,
  formatValue,
  formatInline,
  labelMicro,
  formatValueShort,
  HeaderCell,
  ContentCell,
  Display,
  Filter,
};
